import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { isDefined, localize } from 'common/utils';
import { createOptionBinding } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { AddOptionsToItem } from './AddOptionsToItem';
import { ToastErrorList } from './AddOptionsToItem/ToastErrorList';
import { OptionBindingsList } from './OptionBindingsList';
import { buildOptionBindingCreatePayload } from './utils';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { Offer } from '../../types';

type Props = {
  className?: string;
  enableReordering: boolean;
  offer: Offer;
  isWeightedItem: boolean;
};

const Root = styled.div``;

const Header = styled.div`
  ${typographyCss.Title1};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledInputAlert = styled(InputAlert)`
  margin: 26px 0;
`;

export const OptionBindings = ({ className, enableReordering, offer, isWeightedItem }: Props) => {
  const intl = useIntl();

  const { selection, options, updateSelectionData } = useSelectionData();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const [isItemOptionsModalOpen, setIsItemOptionsModalOpen] = useState(false);

  const getOptionFromOptionId = (optionId: string) => options.find(option => option.id === optionId);

  const { mutateAsync: executeAddOptionsToOffer, isLoading: addOptionsToOfferLoading } = useMutation(
    async (selectedOptionIds: Set<string>) => {
      const addedOptionBindings = [...selectedOptionIds]
        .map(id => {
          const option = getOptionFromOptionId(id);
          return option ? buildOptionBindingCreatePayload(option) : undefined;
        })
        .filter(isDefined);

      const optionsWithErrors: { optionName: string; errorMessage?: string }[] = [];
      let updatedOffer: Offer | undefined;
      for (const payload of addedOptionBindings) {
        try {
          updatedOffer = await createOptionBinding(selection.id, offer.id, payload);
        } catch (error) {
          const option = getOptionFromOptionId(payload.option_id);
          const optionName = option ? localize(option.name, selection.primary_language) : payload.option_id;
          optionsWithErrors.push({ optionName, errorMessage: error?.response?.data?.detail ?? undefined });
        }
      }
      if (updatedOffer) {
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
      }

      if (optionsWithErrors.length > 0) {
        toast.error(<ToastErrorList optionsWithErrors={optionsWithErrors} />, { duration: Infinity });
      }
      setIsItemOptionsModalOpen(false);
    },
  );

  return (
    <Root className={className}>
      <Header>
        <FormattedMessage id="menu-item.options" />
        {!isWeightedItem && !isSharedVenue && menuPermissions.contentEdit && (
          <Button variant="lightBlue" size="small" onClick={() => setIsItemOptionsModalOpen(true)}>
            <FormattedMessage id="menu-item.add-option" />
          </Button>
        )}
      </Header>

      {isWeightedItem && (
        <StyledInputAlert variant="info">
          {intl.formatMessage({ id: 'menu-item.disabled-for-weighted-items' })}
        </StyledInputAlert>
      )}

      {!isWeightedItem && (
        <OptionBindingsList canDrag={enableReordering && menuPermissions.contentEdit} offer={offer} />
      )}

      {!isWeightedItem && !offer.option_bindings?.length && (
        <StyledInputAlert variant="info">{intl.formatMessage({ id: 'menu-item.no-options' })}</StyledInputAlert>
      )}

      <AddOptionsToItem
        isOpen={isItemOptionsModalOpen}
        loading={addOptionsToOfferLoading}
        onClose={() => setIsItemOptionsModalOpen(false)}
        onAddOptionBindings={executeAddOptionsToOffer}
      />
    </Root>
  );
};
